export const APP_SETTING_KEY = {
  Announcements: "announcements",
  Maintenance: "maintenance",
  CreditPricing: "credit_pricing",
  SystemNotifications: "system_notifications",
  CryptoSweep: "crypto_sweep",
} as const;

export type AppSettingKey =
  (typeof APP_SETTING_KEY)[keyof typeof APP_SETTING_KEY];

export const ANNOUNCEMENT_AUDIENCE_ROLES = [
  "admin",
  "sub_admin",
  "agent",
  "dealer",
  "sub_dealer",
  "user",
] as const;

export type AnnouncementAudienceRole =
  (typeof ANNOUNCEMENT_AUDIENCE_ROLES)[number];

export interface AnnouncementItem {
  id: string;
  text: string;
  isActive: boolean;
  sortOrder: number;
  highlight: boolean;
  /**
   * Optional click-through target — the whole announcement becomes a link in
   * the banner (e.g. "https://wa.me/31617150737"). Server-validated to
   * https:/tel:/mailto: only. Null (or absent on old items) = plain text.
   */
  linkUrl?: string | null;
  audienceRoles: AnnouncementAudienceRole[];
}

export type AnnouncementsSettingValue = AnnouncementItem[];

export const MAINTENANCE_MODES = ["manual", "scheduled"] as const;
export type MaintenanceMode = (typeof MAINTENANCE_MODES)[number];

export interface MaintenanceSettingValue {
  mode: MaintenanceMode;
  enabled: boolean;
  startAt: string | null;
  endAt: string | null;
  message: string;
}

export interface CreditPricingTier {
  /** Minimum credit count to enter this tier (inclusive). */
  minCredits: number;
  /** Discount ratio 0-1 (e.g. 0.05 = 5%) — applied to the TRY price. */
  discount: number;
  /** Fixed extra credits granted on purchase in this tier (free; does not
   * affect the price). */
  bonus: number;
}

export interface CreditPricingSettingValue {
  /** 1 kredinin TL fiyatı. */
  pricePerCredit: number;
  /** USDT/TL kuru — tek kaynak bu setting'tir (admin günceller). FE buradan
   * okur; sabit kopya tutmaz. */
  tryPerUsdt: number;
  /** Minimum tek seferlik satın alma adedi. */
  minPurchaseCredits: number;
  /** Maksimum tek seferlik satın alma adedi. */
  maxPurchaseCredits: number;
  /** Tier'lar herhangi sırada; runtime'da minCredits desc'e göre eşleştirilir. */
  tiers: CreditPricingTier[];
  /** 1 native coin'in USDT değeri (BNB/ETH/TRX) — yanlışlıkla native gönderilen
   * yatırımı otomatik kredilemek için. 0/boş → o coin manuel incelemeye düşer. */
  nativeUsdtRates?: {
    bep20?: number;
    erc20?: number;
    trc20?: number;
  };
  /** Dust tabanı (USD-eşdeğeri): altındaki yan-yatırımlar yok sayılır. */
  minDepositUsdt?: number;
  /** Beklenmeyen token'ları yüzeye çıkarma allow-list'i; boş = hepsi. */
  foreignTokenAllowList?: string[];
  /** Foreign-token kayıtları tamamen açık/kapalı (default açık). */
  recordForeignTokens?: boolean;
  /** Native oto-kredi üst sınırı (USD); üstü manuel incelemeye düşer. */
  maxNativeAutoCreditUsdt?: number;
}

export const SYSTEM_NOTIFICATION_TYPES = [
  "info",
  "shield",
  "clock",
  "gift",
  "warning",
] as const;

export type SystemNotificationType = (typeof SYSTEM_NOTIFICATION_TYPES)[number];

export interface SystemNotificationItem {
  id: string;
  title: string;
  body: string;
  type: SystemNotificationType;
  isActive: boolean;
  sortOrder: number;
  /**
   * ISO timestamp set by admin UI whenever the notification is created or
   * re-activated. Frontend uses it together with `id` to scope the per-user
   * read flag in localStorage so re-broadcasting marks it unread again.
   */
  broadcastedAt: string;
  audienceRoles: AnnouncementAudienceRole[];
  /**
   * Optional user-level narrowing on top of the role filter (per-user risk
   * alerts). Server-side filtered; mirrored here for type parity.
   */
  audienceUserIds?: number[];
  /**
   * Also shown as a pop-up dialog in the admin panel until the viewer picks
   * "don't show again". Absent = notification list only.
   */
  displayAsDialog?: boolean;
}

export type SystemNotificationsSettingValue = SystemNotificationItem[];

// Crypto sweep (treasury) config — only PUBLIC addresses; empty = that format
// off. Admin-only; written via the dedicated step-up endpoint.
export interface CryptoSweepSettingValue {
  sweepEvmAddress: string;
  sweepTronAddress: string;
  autoSweepEnabled: boolean;
  sweepThresholdUsdt: number;
}

export const DEFAULT_CRYPTO_SWEEP: CryptoSweepSettingValue = {
  sweepEvmAddress: "",
  sweepTronAddress: "",
  autoSweepEnabled: false,
  sweepThresholdUsdt: 50,
};

export interface AppSettingsAll {
  [APP_SETTING_KEY.Announcements]: AnnouncementsSettingValue;
  [APP_SETTING_KEY.Maintenance]: MaintenanceSettingValue;
  [APP_SETTING_KEY.CreditPricing]: CreditPricingSettingValue;
  [APP_SETTING_KEY.SystemNotifications]: SystemNotificationsSettingValue;
  [APP_SETTING_KEY.CryptoSweep]: CryptoSweepSettingValue;
}

export type VisibleAppSettings = Partial<AppSettingsAll>;

// Frontend fallback — backend default'u ile birebir aynı. Network çağrısı
// gelmeden veya hata durumunda BuyCredits/Pricing UI bozulmadan render eder.
export const DEFAULT_CREDIT_PRICING: CreditPricingSettingValue = {
  pricePerCredit: 1.0,
  tryPerUsdt: 45.4,
  minPurchaseCredits: 5_000,
  maxPurchaseCredits: 10_000_000,
  // Range = [minCredits, next tier's minCredits - 1]; the top tier is open-ended.
  tiers: [
    { minCredits: 5_000, discount: 0, bonus: 0 }, // 5.000–9.999
    { minCredits: 10_000, discount: 0.05, bonus: 0 }, // 10.000–29.999
    { minCredits: 30_000, discount: 0.07, bonus: 0 }, // 30.000–59.999
    { minCredits: 60_000, discount: 0.09, bonus: 0 }, // 60.000–99.999
    { minCredits: 100_000, discount: 0.11, bonus: 0 }, // 100.000–149.999
    { minCredits: 150_000, discount: 0.13, bonus: 0 }, // 150.000–249.999
    { minCredits: 250_000, discount: 0.15, bonus: 0 }, // 250.000–399.999
    { minCredits: 400_000, discount: 0.17, bonus: 0 }, // 400.000–599.999
    { minCredits: 600_000, discount: 0.18, bonus: 0 }, // 600.000–799.999
    { minCredits: 800_000, discount: 0.19, bonus: 0 }, // 800.000–999.999
    { minCredits: 1_000_000, discount: 0.2, bonus: 0 }, // 1.000.000+
  ],
};

// Tier hesabı backend ile aynı: en yüksek minCredits eşik <= credits.
export function resolveCreditTier(
  credits: number,
  config: CreditPricingSettingValue
): CreditPricingTier {
  const sorted = [...config.tiers].sort((a, b) => b.minCredits - a.minCredits);
  return (
    sorted.find((t) => credits >= t.minCredits) ??
    sorted[sorted.length - 1] ??
    DEFAULT_CREDIT_PRICING.tiers[0]
  );
}
